import React from 'react';
import { useState, useEffect } from 'react';
import Editor from 'react-simple-wysiwyg';
import './classes/EditBlog.css';
import { configData } from "../config";
import Loading from './Loading';

const errorIcon = '#fef5f1 url("/basic/web/images/message-24-error.png") left 10px top 10px no-repeat';
const grippie = { background: '#eee url("/basic/web/images/grippie.png") no-repeat center 2px' };

// A body with its own styles, scripts or PHP is edited as HTML source only:
// the rich text editor would apply them to this page and rewrite the markup.
const isSourceOnly = (body) => /<\s*(style|script|link)\b|<\?/i.test(body || '');

// The Edit tab of a basic page (Drupal's "Basic page", pages.type "page"), such as the
// Courses page: its title, body, meta tags and address.
const EditBasicPage = ({ page }) => {
    const app_url = configData.APP_URL;
    const [formData, setFormData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [errors, setErrors] = useState({});
    const [backendErrorMessage, setBackendErrorMessage] = useState('');
    const [showSummary, setShowSummary] = useState(false);
    const [useRichEditor, setUseRichEditor] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const [buttonText, setButtonText] = useState('Save');

    useEffect(() => {
        getPageData();
    }, [page.nid]);

    // The page as stored: the body here is what was entered, not what the View tab shows
    async function getPageData() {
        try {
            setIsLoading(true);
            const response = await fetch(app_url + 'pages/geteditpagedata?nid=' + page.nid, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
            });
            const responsedata = await response.json();
            if (responsedata.data) {
                const data = responsedata.data;
                setFormData({
                    nid: data.nid,
                    title: data.title || '',
                    body: data.body || '',
                    body_summary: data.body_summary || '',
                    field_meta_title: data.field_meta_title || '',
                    field_meta_keywords: data.field_meta_keywords || '',
                    field_meta_description: data.field_meta_description || '',
                    url_alias: data.url_alias || '',
                });
                setShowSummary(!!data.body_summary);
            } else {
                setBackendErrorMessage(responsedata.message || 'The page could not be loaded.');
            }
        } catch (err) {
            setBackendErrorMessage('The page could not be loaded.');
        } finally {
            setIsLoading(false);
        }
    }

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const validationErrors = {};
        if (!formData.title.trim()) {
            validationErrors.title = 'Title field is required.';
        }
        setErrors(validationErrors);
        setBackendErrorMessage('');
        if (Object.keys(validationErrors).length > 0) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setDisabled(true);
        setButtonText('Saving...');
        try {
            const response = await fetch(app_url + 'pages/updatepage', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (data.data?.msg === 'Success') {
                window.location.href = '/' + data.data.url_slug;
                return;
            }
            setBackendErrorMessage(data.data?.text || data.message || 'The page could not be saved.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (err) {
            setBackendErrorMessage('The page could not be saved.');
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setDisabled(false);
            setButtonText('Save');
        }
    };

    if (isLoading) return <Loading />;

    const sourceOnly = isSourceOnly(formData?.body);

    return (
        <div id="main" className="edit-blog-main">
            <div className="left"></div>
            <div className="center left-center">
                <h1 className="edit-task-title">Edit Basic page <em>{page.title}</em></h1>
                <ul className="tabs primarya">
                    <li onClick={() => { window.location.href = '/' + page.url_slug; }}>
                        <a style={{ cursor: "pointer" }}>View</a>
                    </li>
                    <li className="active">
                        <a style={{ cursor: "pointer" }}>Edit</a>
                    </li>
                </ul>

                <div className="edit-blog-container">
                    {Object.values(errors).some((msg) => !!msg) && (
                        <div className="set-message-error" style={{ background: errorIcon, padding: "10px 10px 10px 50px", width: "90%" }}>
                            <ul>
                                {Object.values(errors).filter((msg) => !!msg).map((msg, index) => (
                                    <li key={index}>{msg}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {backendErrorMessage && (
                        <div className="set-message-error" style={{ background: errorIcon, padding: "10px 10px 10px 50px", width: "90%" }}>
                            {backendErrorMessage}
                        </div>
                    )}

                    {formData && (
                        <form onSubmit={handleSubmit}>
                            <div className="form-item">
                                <label htmlFor="edit-page-title">
                                    Title <span className="form-required" title="This field is required.">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="edit-page-title"
                                    name="title"
                                    maxLength={255}
                                    value={formData.title}
                                    onChange={handleChange}
                                    style={{ border: errors.title ? '2px solid red' : '1px solid #777' }}
                                />
                            </div>

                            {showSummary && (
                                <div className="form-item">
                                    <label htmlFor="edit-page-summary">
                                        Summary <span>(<a href="#" onClick={(e) => { e.preventDefault(); setShowSummary(false); }}>Hide Summary</a>)</span>
                                    </label>
                                    <textarea
                                        id="edit-page-summary"
                                        name="body_summary"
                                        rows="5"
                                        value={formData.body_summary}
                                        onChange={handleChange}
                                    />
                                    <div className="grippie" style={grippie}></div>
                                    <div className="description">Leave blank to use trimmed value of full text as the summary.</div>
                                </div>
                            )}

                            <div className="form-item">
                                <label htmlFor="edit-page-body">
                                    Body
                                    {!showSummary && (
                                        <span> (<a href="#" onClick={(e) => { e.preventDefault(); setShowSummary(true); }}>Edit Summary</a>)</span>
                                    )}
                                </label>
                                {useRichEditor && !sourceOnly ? (
                                    <>
                                        <Editor
                                            containerProps={{ style: { resize: 'vertical' } }}
                                            value={formData.body}
                                            onChange={(e) => setFormData((prev) => ({ ...prev, body: e.target.value }))}
                                        />
                                        <a href="#" onClick={(e) => { e.preventDefault(); setUseRichEditor(false); }}>
                                            Switch to plain text editor
                                        </a>
                                    </>
                                ) : (
                                    <>
                                        <textarea
                                            id="edit-page-body"
                                            name="body"
                                            rows="20"
                                            value={formData.body}
                                            onChange={handleChange}
                                        />
                                        <div className="grippie" style={grippie}></div>
                                        {sourceOnly ? (
                                            <div className="description">This body has its own styles or scripts, so it is edited as HTML.</div>
                                        ) : (
                                            <a href="#" onClick={(e) => { e.preventDefault(); setUseRichEditor(true); }}>
                                                Switch to rich text editor
                                            </a>
                                        )}
                                    </>
                                )}
                            </div>

                            <div className="form-item">
                                <label htmlFor="edit-page-meta-title">Meta Title</label>
                                <input
                                    type="text"
                                    id="edit-page-meta-title"
                                    name="field_meta_title"
                                    maxLength={255}
                                    value={formData.field_meta_title}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-meta-keywords">Meta Keywords</label>
                                <input
                                    type="text"
                                    id="edit-page-meta-keywords"
                                    name="field_meta_keywords"
                                    maxLength={255}
                                    value={formData.field_meta_keywords}
                                    onChange={handleChange}
                                />
                            </div>
                            <div className="form-item">
                                <label htmlFor="edit-page-meta-description">Meta Description</label>
                                <textarea
                                    id="edit-page-meta-description"
                                    name="field_meta_description"
                                    value={formData.field_meta_description}
                                    onChange={handleChange}
                                />
                                <div className="grippie" style={grippie}></div>
                            </div>

                            <div className="form-item">
                                <label htmlFor="edit-page-alias">URL alias</label>
                                <input
                                    type="text"
                                    id="edit-page-alias"
                                    name="url_alias"
                                    value={formData.url_alias}
                                    onChange={handleChange}
                                />
                                <p className="description" style={{ marginTop: "5px" }}>Optionally specify an alternative URL by which this content can be accessed. For example, type "about" when writing an about page.
                                    Use a relative path and don't add a trailing slash or the URL alias won't work.</p>
                            </div>

                            <div className="form-actions form-wrapper">
                                <button type="submit" className="form-submit" disabled={disabled}>{buttonText}</button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditBasicPage;
